import { ILink, Navbar, IRouter } from '@back4app2/react-components';
import { AppPlan, AppPlanStatus, AuthenticationError, AuthorizationError, Back4app2Error, NetworkError, NotFoundError, User } from '@back4app2/sdk';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import back4app2, { useBack4app2ConnectionStatus } from './back4app2';
import { API_HTTP_URL, BACK4APP_DOT_COM_DASHBOARD_URL, BACK4APP_DOT_COM_OLD_SITE_URL, BACK4APP_DOT_COM_SITE_URL, EMAIL_VERIFICATION_REDIRECT_URL, PARSE_DASHBOARD_URL } from './settings';
import { initializeAmplitude, setReplayEnabled, setUserForTracking } from './utils/amplitude';
import { initializeLogRocketSession } from './utils/logRocket';
import { toast } from 'react-hot-toast';
import { ReactComponent as StatusErrorSVG } from './assets/images/status-error.svg';
import { AgentViewProvider, useShouldHideNavbar } from './contexts';
import { useIsMobile } from './hooks/useIsMobile';

const runAfterLoadAndIdle = (fn: () => void) => {
  const run = () => {
    const ric = (window as any).requestIdleCallback;
    if (typeof ric === 'function') ric(fn, { timeout: 5000 });
    else setTimeout(fn, 0);
  };

  if (document.readyState === 'complete') run();
  else window.addEventListener('load', run, { once: true });
};

interface AppState {
  user?: User;
  appsPlans?: AppPlan[];
  freePlanUsageHours?: number;
}

const INITIAL_STATE: AppState = {};

enum AppActionType {
  CHANGE_CONNECTION_STATUS,
  FINISH_LOADING_USER,
  FINISH_LOADING_APPS_PLANS,
  SET_FREE_PLAN_USAGE_HOURS
}

const finishLoadingUser = (user?: User) => ({
  type: AppActionType.FINISH_LOADING_USER,
  payload: {
    user
  }
} as const);

const finishLoadingAppsPlans = (appsPlans?: AppPlan[]) => ({
  type: AppActionType.FINISH_LOADING_APPS_PLANS,
  payload: {
    appsPlans
  }
} as const);

const setFreePlanUsageHours = (value?: number) => ({
  type: AppActionType.SET_FREE_PLAN_USAGE_HOURS,
  payload: {
    value
  }
} as const);

type AppAction = ReturnType<typeof finishLoadingUser> | ReturnType<typeof finishLoadingAppsPlans> | ReturnType<typeof setFreePlanUsageHours>;

const reducer = (state: AppState = INITIAL_STATE, action: AppAction): AppState => {
  switch (action.type) {
    case AppActionType.FINISH_LOADING_USER:
      return {
        ...state,
        user: action.payload.user
      };

    case AppActionType.FINISH_LOADING_APPS_PLANS:
      return {
        ...state,
        appsPlans: action.payload.appsPlans
      };
    case AppActionType.SET_FREE_PLAN_USAGE_HOURS:
      return {
        ...state,
        freePlanUsageHours: action.payload.value
      }
  }
};

const parseHref = (href: string) => {
  if (href.startsWith(window.location.origin)) {
    return href.replace(window.location.origin, '');
  } else {
    return href;
  }
}

const LinkImpl: ILink = ({ href, className, children }) => {
  href = parseHref(href);
  
  if (href.startsWith('http')) {
    return <a href={href} className={className}>
      {children}
    </a>;
  } else {
    return <Link to={href} className={className}>
      {children}
    </Link>;
  }
}

// Screens recorded by Session Replay.
//
// Matched exactly, never by prefix. The app overview is '/apps/:appId' and
// nothing below it: '/apps/:appId/settings' shows saved environment variable
// values, and deployments and logs carry build output that can echo them. A
// prefix match on '/apps/' would pull all of those in silently, which is the
// mistake this function exists to make impossible.
const APP_OVERVIEW_PATH = /^\/apps\/[^/]+\/?$/;

function isReplayScreen(pathname: string): boolean {
  return pathname === '/new-container' || APP_OVERVIEW_PATH.test(pathname);
}

function App() {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const { user, appsPlans, freePlanUsageHours } = state;
  const { isReconnecting } = useBack4app2ConnectionStatus();

  const location = useLocation();
  const pathname = location.pathname;

  const isAgentRoute = pathname === '/agents' || pathname.startsWith('/agents/');

  // The agent screens are pinned to the type scale they were designed at,
  // whatever the Containers multiplier in index.css is set to.
  useEffect(() => {
    document.documentElement.classList.toggle('b4a2-base-type', isAgentRoute);
    return () => document.documentElement.classList.remove('b4a2-base-type');
  }, [isAgentRoute]);

  // Covers the whole create funnel: the form, and the app overview it lands on
  // once the app exists — which is where the first deploy plays out, and the
  // part that says whether creating the app actually worked.
  const isReplaySurface = isReplayScreen(pathname);

  useEffect(() => {
    setReplayEnabled(isReplaySurface);

    // Detach on unmount too, so recording never outlives the dashboard tab's
    // last replayed screen.
    return () => setReplayEnabled(false);
  }, [isReplaySurface]);

  const navigate = useNavigate();
  const push = useCallback((url: string) => {
    url = parseHref(url);

    if (url.startsWith('http')) {
      window.location.assign(url);
    } else {
      navigate(url);
    }
  }, [navigate]);
  const replace = useCallback((url: string) => {
    url = parseHref(url);

    if (url.startsWith('http')) {
      window.location.replace(url);
    } else {
      navigate(url, { replace: true });
    }
  }, [navigate]);

  const router = useMemo<IRouter>(() => ({
    pathname,
    push,
    replace
  }), [pathname, push, replace]);

  const runAfterIdle = (fn: () => void) => {
    const ric = (window as any).requestIdleCallback;
    if (typeof ric === 'function') ric(fn, { timeout: 10000 });
    else setTimeout(fn, 3000);
  };
  
  const loadGTMOnce = () => {
    if ((window as any).__b4a2GtmLoaded) return;
    (window as any).__b4a2GtmLoaded = true;
  

    (function (w: Window, d: Document, l: string, i: string) {
      const ww = w as any;
    
      ww[l] = ww[l] || [];
      (ww[l] as any[]).push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    
      const f = d.getElementsByTagName('script')[0];
      const j = d.createElement('script');
      const dl = l !== 'dataLayer' ? '&l=' + l : '';
    
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    
      if (f?.parentNode) f.parentNode.insertBefore(j, f);
      else (d.head || d.documentElement).appendChild(j);
    })(window, document, 'dataLayer', 'GTM-W79Z9T');

  };
  
  const loadZendeskOnce = () => {
    if ((window as any).__b4a2ZendeskLoaded) return;
    (window as any).__b4a2ZendeskLoaded = true;
  
    const zKey = process.env.REACT_APP_BACK4APP2_DASHBOARD_ZENDESK_KEY;
    if (!zKey) return;
  
    const existing = document.getElementById('ze-snippet');
    if (existing) return;
  
    const zs = document.createElement('script');
    zs.id = 'ze-snippet';
    zs.async = true;
    zs.src =
      'https://static.zdassets.com/ekr/snippet.js?key=' +
      encodeURIComponent(zKey);
    document.body.appendChild(zs);
  };

  useEffect(() => {
    (async () => {
      let fetchedUser: User;
      try {
        fetchedUser = await back4app2.me();
        import('@sentry/react').then(({ setUser: setSentryUser }) => {
          setSentryUser({
            id: fetchedUser.username,
            email: fetchedUser.username,
            username: fetchedUser.username,
            ip_address: '{{auto}}',
          });
        });
        runAfterLoadAndIdle(() => {
          initializeAmplitude(fetchedUser.username);
          setUserForTracking(fetchedUser.id, fetchedUser.username);
          initializeLogRocketSession(fetchedUser.username);
        });
      } catch (e) {
        if (e instanceof NetworkError) {
          console.error('network error when getting user', e);
        } else if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
          console.error('authentication or authorization error when getting user', e);
        } else if (e instanceof Back4app2Error && e.code === 'NOT_CONTAINERS_BETA_USER') {
          console.error('not a containers beta user', e);
          window.location.replace(BACK4APP_DOT_COM_SITE_URL);
          return;
        } else {
          console.error('unexpected error when getting user', e);
        }
        window.location.replace(
          `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`
        );
        return;
      }
      // New (post-cutoff) users must confirm their email before entering the
      // dashboard. Mirror the not-logged-in redirect: bounce to the verify page
      // and abort bootstrap so the shell never renders. Existing users have
      // requiresEmailVerification === false and pass straight through.
      if (fetchedUser.requiresEmailVerification) {
        window.location.replace(
          `${EMAIL_VERIFICATION_REDIRECT_URL}?dest=${encodeURIComponent(window.location.href)}`
        );
        return;
      }
      dispatch(finishLoadingUser(fetchedUser));
      try {
        const fetchedAppsPlans = await back4app2.findAppsPlans();
        dispatch(finishLoadingAppsPlans(fetchedAppsPlans));
      } catch (e) {
        if (e instanceof NetworkError) {
          console.error('network error when finding apps plans', e);
        } else if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
          console.error('authentication or authorization error when finding apps plans', e);
          window.location.replace(
            `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`
          );
        } else {
          console.error('unexpected error when finding apps plans', e);
        }
        return;
      }
    })();
  }, []);

  // Load third-party scripts only after bootstrap is complete.
  // Never load Zendesk while the user is on Agent routes.
  useEffect(() => {
    if (!appsPlans) return;
    runAfterIdle(() => {
      loadGTMOnce();
      if (!isAgentRoute) {
        loadZendeskOnce();
      }
    });
  }, [appsPlans, isAgentRoute]);
  
  return (
    <AgentViewProvider>
      <AppContent
        user={user}
        appsPlans={appsPlans}
        freePlanUsageHours={freePlanUsageHours}
        router={router}
        dispatch={dispatch}
      />
    </AgentViewProvider>
  );
}

// Inner component that can access AgentViewContext
interface AppContentProps {
  user?: User;
  appsPlans?: AppPlan[];
  freePlanUsageHours?: number;
  router: IRouter;
  dispatch: React.Dispatch<AppAction>;
}

function AppContent({ user, appsPlans, freePlanUsageHours, router, dispatch }: AppContentProps) {
  const isMobile = useIsMobile();
  const shouldHideNavbar = useShouldHideNavbar(isMobile);
  const { pathname } = useLocation();

  // The container screens carry their own visual system: solid surfaces and —
  // on mobile — a bottom tab bar. The Agents screens keep the ambient blob and
  // the support launcher they have.
  const isContainerScreen = pathname === '/apps'
    || pathname.startsWith('/apps/')
    || pathname === '/new-container';

  // The bottom tab bar owns the corner the support launcher sits in.
  useEffect(() => {
    document.body.classList.toggle('b4a2-container-screen', isContainerScreen);
    return () => document.body.classList.remove('b4a2-container-screen');
  }, [isContainerScreen]);

  return (
    <>
      <div className="w-full h-full flex flex-col overflow-hidden">
        {/* Navbar - hidden on mobile when viewing Preview or Database in Agent screen */}
        {!shouldHideNavbar && (
          <Navbar
            user={(user && {
              ...user,
              containersBetaUser: true
            }) || undefined}
            overLimitAppsPlansCount={(appsPlans && appsPlans.filter(appPlan => appPlan.status === AppPlanStatus.OVER_LIMITS).length) || undefined}
            router={router}
            Link={LinkImpl}
            back4appDotComSiteURL={BACK4APP_DOT_COM_SITE_URL}
            back4appDotComOldSiteURL={BACK4APP_DOT_COM_OLD_SITE_URL}
            back4appDotComDashboardURL={BACK4APP_DOT_COM_DASHBOARD_URL}
            back4appContainersApiURL={API_HTTP_URL}
          />
        )}
        <Outlet context={{ user, freePlanUsageHours, updateUser: (user: User) => dispatch(finishLoadingUser(user)) }} />
        {!isContainerScreen && (
          <div
            className="absolute -left-[16.5625rem] -top-[9.5625rem] lg:-top-[23.4375rem] w-[36.8125rem] lg:w-[50.125rem] h-[34.6875rem] lg:h-[47.25rem] rounded-[374px] opacity-30 -z-[1]"
            style={{
              background: 'radial-gradient(48.2% 50% at 50% 50%, #3496EF 0%, rgba(15, 28, 50, 0) 100%)'
            }}
          />
        )}
      </div>    
    </>
  );
}

export default App;
